/**
 * Role roster contract for the live role-view selector.
 *
 * The authoritative roster is served by the backend at `GET /api/auth/personas`,
 * sourced from `core.constants.DEMO_PERSONAS`. The UI renders that live response
 * (role labels + each role's `entra_groups`) — never person names or emails.
 * Switching role is an authenticated, password-less `POST /auth/assume-role`.
 */

import { api } from './api';
import { ENTRA_GROUP_LABELS, type EntraGroup } from './constants';

/** The platform-admin role key — the "Admin (all access)" view. */
export const ADMIN_ROLE_KEY = 'platform_admin';

/**
 * A selectable role as returned by `GET /api/auth/personas`.
 *
 * Mirrors the backend `PersonaOption` model: `key` is the stable identifier
 * posted back to `/auth/login`, and `entra_groups` is the set of group claims
 * that persona carries (the authorization primitive the permission filter
 * intersects against document ACLs).
 */
export interface PersonaOption {
  key: string;
  display_name: string;
  role: string;
  entra_groups: string[];
}

/** Fetch the live persona roster from the backend (public; no auth required). */
export async function fetchPersonas(): Promise<PersonaOption[]> {
  return api.get<PersonaOption[]>('/auth/personas', { skipAuth: true });
}

/**
 * Human-readable label for an Entra group string. Falls back to a humanized
 * form of the raw group name when it isn't in the static label map (e.g. the
 * backend's `entra-grp-all-staff`), so the switcher never renders a blank chip.
 */
export function groupLabel(group: string): string {
  const known = ENTRA_GROUP_LABELS[group as EntraGroup];
  if (known) return known;
  return group
    .replace(/^entra-grp-/, '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
